import {AdjacentShopperDetails, OrderItems} from '../../../batchDetails/types';
import {Box, HBox} from '../../../components/core/view';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useColorThemeType, useFontTheme} from '../../../appStyles/hooks/theme';

import BatchOrderItem from '../BatchOrderItem';
import {CustomText} from '@buncha/components/core/text';
import {GestureResponderEvent} from 'react-native/Libraries/Types/CoreEventTypes';
import If from '../../../components/conditional/If';
import LocationTypo from '../LocationTypo';
import MinusTickIcon from '../../../components/core/icon/MinusTickIcon';
import {OrderItemStatus} from '@buncha/common/types/common';
import RightTickIcon from '../../../components/core/icon/RightTickIcon';
import {Touchable} from '../../../components/core/touchable';
import {isWeb} from '@buncha/utils/common';
import {styles} from './style';
import {useBatchDetailContext} from '../../../batchDetails/hooks/batchDetail';

interface PackOrderItemProps {
  packingFinished: boolean;
  orderItem: OrderItems;
  onClick: (orderItem: OrderItems, status: OrderItemStatus) => void;
  dropItem: (orderItem: OrderItems) => void;
  unpackItem: (orderItem: OrderItems) => void;
  adjacentShopperDetails?: AdjacentShopperDetails;
  runId: number | undefined;
}

function PackOrderItem(props: PackOrderItemProps) {
  const {
    packingFinished,
    orderItem,
    onClick,
    dropItem,
    unpackItem,
    adjacentShopperDetails,
    runId,
  } = props;
  const [stateChanged, setStateChanged] = useState(false);
  const theme = useColorThemeType();
  const {state} = useBatchDetailContext();
  const muliFont = useFontTheme('muli');

  const isAdjacentOOSItem =
    orderItem?.isAdjacentOOSItem && !adjacentShopperDetails;

  const opacityStyle = useMemo(() => {
    return {
      opacity: packingFinished || isAdjacentOOSItem ? 0.5 : 1,
    };
  }, [isAdjacentOOSItem, packingFinished]);

  const onItemClick = useCallback(
    (clickedOrderItem: OrderItems) => {
      if (isAdjacentOOSItem) return;
      if (clickedOrderItem.itemStatus === OrderItemStatus.dropped)
        return unpackItem(orderItem);
      onClick(
        clickedOrderItem,
        stateChanged ? OrderItemStatus.notPacked : OrderItemStatus.packed,
      );
      setStateChanged(!stateChanged);
    },
    [isAdjacentOOSItem, onClick, orderItem, stateChanged, unpackItem],
  );

  const getRightIcon = useCallback(
    (item: any) => {
      switch (item.itemStatus) {
        case OrderItemStatus.dropped:
          return (
            <MinusTickIcon
              width={24}
              height={24}
              viewBox="-3 -1 25 25"
              fill={theme?.background.red[100]}
            />
          );
        case OrderItemStatus.packed:
          if (stateChanged)
            return (
              <RightTickIcon
                width={24}
                height={24}
                viewBox="-5 0 25 20"
                fill={theme?.background.blue[100]}
              />
            );
          return (
            <Box
              style={[
                styles.hollowCircle,
                styles.circleBox,
                {
                  borderColor: theme?.background.gray[100],
                },
              ]}
            />
          );
        case OrderItemStatus.notPacked:
          if (stateChanged)
            return (
              <RightTickIcon
                width={24}
                height={24}
                viewBox="-3 -1 25 25"
                fill={theme?.background.blue[100]}
              />
            );
          return (
            <Box
              style={[
                styles.hollowCircle,
                styles.circleBox,
                {
                  borderColor: theme?.background.gray[100],
                },
              ]}
            />
          );
        default:
          return (
            <Box
              style={[
                styles.hollowCircle,
                {
                  borderColor: theme?.background.gray[100],
                },
              ]}
            />
          );
      }
    },
    [
      stateChanged,
      theme?.background.blue,
      theme?.background.gray,
      theme?.background.red,
    ],
  );
  const handleDrop = useCallback(
    () => dropItem(orderItem),
    [dropItem, orderItem],
  );

  useEffect(() => {
    if (orderItem.itemStatus === OrderItemStatus.notPacked)
      setStateChanged(false);
    else setStateChanged(true);
  }, [orderItem]);

  return (
    <Box>
      <BatchOrderItem
        runId={runId}
        hideLocation
        disabledOpacity={isAdjacentOOSItem}
        esIndex={state?.upcOrderData?.upcOrder?.esIndex ?? ''}
        orderItem={orderItem}
        rightIcon={
          <HBox>
            <Touchable
              hitSlop={{
                bottom: 10,
                left: 10,
                right: 10,
                top: 10,
              }}
              style={[styles.touchableStyle, opacityStyle]}
              disabled={packingFinished || isAdjacentOOSItem}
              activeOpacity={1}
              onPress={(event: GestureResponderEvent) => {
                event.preventDefault();
                onItemClick(orderItem);
              }}>
              {getRightIcon(orderItem)}
            </Touchable>
          </HBox>
        }
        onClick={() => {
          return;
        }}
        disableOnclick={!isWeb()}
        orderPackedStatus={packingFinished}
      />
      <HBox style={styles.footer}>
        <If
          condition={
            (orderItem?.itemLocationInformation?.aisle ||
              orderItem?.itemLocationInformation?.storeContext) &&
            !orderItem?.itemLocation
          }>
          <LocationTypo
            aisle={orderItem?.itemLocationInformation?.aisle ?? ''}
            context={orderItem?.itemLocationInformation?.storeContext ?? ''}
            orderItemId={orderItem?.id}
            esIndex={state?.upcOrderData?.upcOrder?.esIndex ?? ''}
          />
        </If>
        <If condition={orderItem.itemStatus !== OrderItemStatus.dropped}>
          <Touchable
            disabled={props?.packingFinished || isAdjacentOOSItem}
            style={[styles.dropContainer, opacityStyle]}
            onPress={handleDrop}>
            <CustomText
              style={{
                fontFamily: muliFont?.[700]?.normal,
                color: theme?.text?.red?.[100],
              }}>
              Drop Item
            </CustomText>
          </Touchable>
        </If>
      </HBox>
    </Box>
  );
}

export default React.memo(PackOrderItem);
